/**
 * Enhanced Developer Console for Mernaki
 * Fixed position with advanced dev tools and console toggle
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

interface ConsoleLog {
  id: string;
  type: 'log' | 'warn' | 'error' | 'info';
  message: string;
  timestamp: number;
}

interface PerformanceMetrics {
  memoryUsage: number;
  renderTime: number;
  cacheHits: number;
  totalPages: number;
  fps: number;
  networkRequests: number;
}

export const DeveloperConsole: React.FC = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'cache' | 'console' | 'performance' | 'network'>('cache');
  const [consoleLogs, setConsoleLogs] = useState<ConsoleLog[]>([]);
  const [consoleEnabled, setConsoleEnabled] = useState(true);
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    memoryUsage: 0,
    renderTime: 0,
    cacheHits: 0,
    totalPages: 0,
    fps: 0,
    networkRequests: 0
  });
  
  const consoleRef = useRef<HTMLDivElement>(null);
  const fpsRef = useRef<number[]>([]);
  const lastFrameTime = useRef<number>(0);

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  // Monitor performance metrics
  useEffect(() => {
    const updateMetrics = () => {
      const newMetrics: Partial<PerformanceMetrics> = {};

      // Memory usage
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        newMetrics.memoryUsage = Math.round(memory.usedJSHeapSize / 1024 / 1024);
      }

      // FPS calculation
      const now = performance.now();
      if (lastFrameTime.current) {
        const fps = 1000 / (now - lastFrameTime.current);
        fpsRef.current.push(fps);
        if (fpsRef.current.length > 60) {
          fpsRef.current.shift();
        }
        newMetrics.fps = Math.round(fpsRef.current.reduce((a, b) => a + b, 0) / fpsRef.current.length);
      }
      lastFrameTime.current = now;

      // Render time
      const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigationEntry) {
        newMetrics.renderTime = Math.round(navigationEntry.loadEventEnd - navigationEntry.loadEventStart);
      }

      // Network requests
      const resourceEntries = performance.getEntriesByType('resource');
      newMetrics.networkRequests = resourceEntries.length;

      setMetrics(prev => ({ ...prev, ...newMetrics }));
    };

    const interval = setInterval(updateMetrics, 1000);
    updateMetrics();

    return () => clearInterval(interval);
  }, []);

  // Intercept console methods
  useEffect(() => {
    if (!consoleEnabled) return;

    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info
    };

    const interceptConsole = (type: 'log' | 'warn' | 'error' | 'info') => {
      console[type] = (...args: any[]) => {
        // Call original console method
        originalConsole[type](...args);
        
        // Add to our console logs
        const logEntry: ConsoleLog = {
          id: `${Date.now()}-${Math.random()}`,
          type,
          message: args.map(arg => 
            typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
          ).join(' '),
          timestamp: Date.now()
        };

        setConsoleLogs(prev => {
          const newLogs = [...prev, logEntry];
          // Keep only last 100 logs
          return newLogs.slice(-100);
        });
      };
    };

    interceptConsole('log');
    interceptConsole('warn');
    interceptConsole('error');
    interceptConsole('info');

    return () => {
      console.log = originalConsole.log;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;
      console.info = originalConsole.info;
    };
  }, [consoleEnabled]);

  // Clear console logs
  const clearConsole = () => {
    setConsoleLogs([]);
    console.clear();
  };

  // Toggle browser dev tools
  const toggleDevTools = () => {
    // This will trigger the browser's dev tools detection
    const devtools = {
      open: false,
      orientation: null
    };
    
    setInterval(() => {
      if (window.outerHeight - window.innerHeight > 200) {
        if (!devtools.open) {
          devtools.open = true;
          console.log('🔧 Browser DevTools opened');
        }
      } else {
        if (devtools.open) {
          devtools.open = false;
          console.log('🔧 Browser DevTools closed');
        }
      }
    }, 500);
  };

  // Export logs
  const exportLogs = () => {
    const logsData = {
      timestamp: new Date().toISOString(),
      route: location.pathname,
      metrics,
      logs: consoleLogs
    };
    
    const blob = new Blob([JSON.stringify(logsData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mernaki-dev-logs-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getConsoleIcon = (type: string) => {
    switch (type) {
      case 'error': return '❌';
      case 'warn': return '⚠️';
      case 'info': return 'ℹ️';
      default: return '📝';
    }
  };

  const getConsoleColor = (type: string) => {
    switch (type) {
      case 'error': return '#ff6b6b';
      case 'warn': return '#ffd93d';
      case 'info': return '#74c0fc';
      default: return '#51cf66';
    }
  };

  return (
    <>
      {/* Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-4 right-4 z-[10000] flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg"
        style={{
          backgroundColor: isOpen ? '#1a1a1a' : '#2d3748',
          color: '#ffffff',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          fontFamily: "'SF Mono', 'Monaco', 'Cascadia Code', monospace",
          fontSize: '12px',
          cursor: 'pointer'
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <div 
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: '#00ff88' }}
        />
        Dev Mode
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          ▼
        </motion.div>
      </motion.button>

      {/* Developer Console Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed top-16 right-4 z-[9999] w-96 max-h-[70vh] rounded-xl shadow-2xl overflow-hidden"
            style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold text-sm">Developer Console</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={exportLogs}
                  className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                >
                  Export
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-700">
              {[
                { id: 'cache', label: 'Cache', icon: '📄' },
                { id: 'console', label: 'Console', icon: '🖥️' },
                { id: 'performance', label: 'Perf', icon: '⚡' },
                { id: 'network', label: 'Network', icon: '🌐' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className="flex-1 px-3 py-2 text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: activeTab === tab.id ? '#2d3748' : 'transparent',
                    color: activeTab === tab.id ? '#ffffff' : '#a0aec0',
                    borderBottom: activeTab === tab.id ? '2px solid #00ff88' : 'none'
                  }}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="p-4 max-h-80 overflow-y-auto" style={{ fontSize: '11px', fontFamily: "'SF Mono', monospace" }}>
              {activeTab === 'cache' && (
                <div className="space-y-3">
                  <div className="text-green-400 font-semibold">📄 Page Cache Status</div>
                  <div className="space-y-1 text-gray-300">
                    <div>Current Route: <span className="text-blue-400">{location.pathname}</span></div>
                    <div>Cache Hits: <span className="text-green-400">{metrics.cacheHits}</span></div>
                    <div>Total Pages: <span className="text-yellow-400">{metrics.totalPages}</span></div>
                    <div>Memory Usage: <span className="text-red-400">{metrics.memoryUsage}MB</span></div>
                  </div>
                  
                  <div className="mt-4">
                    <div className="text-purple-400 font-semibold mb-2">🎯 Quick Actions</div>
                    <div className="space-y-2">
                      <button
                        onClick={() => window.location.reload()}
                        className="w-full text-left px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs"
                      >
                        🔄 Clear Cache & Reload
                      </button>
                      <button
                        onClick={toggleDevTools}
                        className="w-full text-left px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs"
                      >
                        🔧 Toggle Browser DevTools
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'console' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-green-400 font-semibold">🖥️ Console Output</div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setConsoleEnabled(!consoleEnabled)}
                        className={`text-xs px-2 py-1 rounded ${consoleEnabled ? 'bg-green-600' : 'bg-gray-600'} text-white`}
                      >
                        {consoleEnabled ? 'ON' : 'OFF'}
                      </button>
                      <button
                        onClick={clearConsole}
                        className="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  
                  <div ref={consoleRef} className="space-y-1 max-h-60 overflow-y-auto">
                    {consoleLogs.length === 0 ? (
                      <div className="text-gray-500 italic">No console output yet...</div>
                    ) : (
                      consoleLogs.map(log => (
                        <div
                          key={log.id}
                          className="p-2 rounded text-xs"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderLeft: `3px solid ${getConsoleColor(log.type)}`
                          }}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span>{getConsoleIcon(log.type)}</span>
                            <span className="text-gray-400">
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <div className="text-gray-200 whitespace-pre-wrap break-words">
                            {log.message}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'performance' && (
                <div className="space-y-3">
                  <div className="text-green-400 font-semibold">⚡ Performance Metrics</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-2 rounded bg-gray-800">
                      <div className="text-yellow-400 text-xs">FPS</div>
                      <div className="text-white font-bold">{metrics.fps}</div>
                    </div>
                    <div className="p-2 rounded bg-gray-800">
                      <div className="text-blue-400 text-xs">Memory</div>
                      <div className="text-white font-bold">{metrics.memoryUsage}MB</div>
                    </div>
                    <div className="p-2 rounded bg-gray-800">
                      <div className="text-green-400 text-xs">Render</div>
                      <div className="text-white font-bold">{metrics.renderTime}ms</div>
                    </div>
                    <div className="p-2 rounded bg-gray-800">
                      <div className="text-purple-400 text-xs">Requests</div>
                      <div className="text-white font-bold">{metrics.networkRequests}</div>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <div className="text-orange-400 font-semibold mb-2">🔍 Performance Tools</div>
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          if ('gc' in window) {
                            (window as any).gc();
                            console.log('🗑️ Garbage collection triggered');
                          } else {
                            console.warn('⚠️ Garbage collection not available');
                          }
                        }}
                        className="w-full text-left px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs"
                      >
                        🗑️ Force Garbage Collection
                      </button>
                      <button
                        onClick={() => {
                          console.log('📊 Performance Timing:', performance.timing);
                          console.log('📈 Performance Entries:', performance.getEntries());
                        }}
                        className="w-full text-left px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs"
                      >
                        📊 Log Performance Data
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'network' && (
                <div className="space-y-3">
                  <div className="text-green-400 font-semibold">🌐 Network Monitor</div>
                  <div className="space-y-2">
                    <div className="text-gray-300">
                      <div>Total Requests: <span className="text-blue-400">{metrics.networkRequests}</span></div>
                      <div>Connection: <span className="text-green-400">{navigator.onLine ? 'Online' : 'Offline'}</span></div>
                      <div>User Agent: <span className="text-gray-400 text-xs break-all">{navigator.userAgent.substring(0, 50)}...</span></div>
                    </div>
                    
                    <div className="mt-4">
                      <div className="text-cyan-400 font-semibold mb-2">🔗 Network Tools</div>
                      <div className="space-y-2">
                        <button
                          onClick={() => {
                            const resources = performance.getEntriesByType('resource');
                            console.log('🌐 Network Resources:', resources);
                          }}
                          className="w-full text-left px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs"
                        >
                          📋 Log Network Resources
                        </button>
                        <button
                          onClick={() => {
                            navigator.serviceWorker.getRegistrations().then(registrations => {
                              console.log('👷 Service Workers:', registrations);
                            });
                          }}
                          className="w-full text-left px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs"
                        >
                          👷 Check Service Workers
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};